/**
 * Current Session – shows active market session(s), open/closed, and countdown to next change.
 * Used in the Tradar bar (instrument + filters + stop button).
 */

import React, { useEffect, useState } from "react";
import { Box, HStack, Text, Tooltip, useColorModeValue, VStack } from "@chakra-ui/react";
import {
  getMarketSessionCurrentState,
  getMarketSessionDefinitions,
  MarketSessionCurrentState,
  MarketSessionDefinition,
} from "../../../../services/marketSessionsApi";

const REFRESH_INTERVAL_MS = 60_000; // 1 min
const TICK_FAST_MS = 10_000; // tick every 10s when under 1h left

/** Format ms as "Xd Xh Xm", "Xh Xm", "Xm", or "&lt; 1m" */
function formatTimeLeft(ms: number): string {
  if (ms <= 0) return "< 1m";
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const parts: string[] = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0) parts.push(`${h}h`);
  if (m > 0 || parts.length === 0) parts.push(`${m}m`);
  return parts.join(" ");
}

/** Format ISO date in UTC for tooltip, e.g. "Tue 22:00 UTC" */
function formatSessionTimeUtc(isoUtc: string): string {
  const d = new Date(isoUtc);
  const weekday = d.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" });
  const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "UTC" });
  return `${weekday} ${time} UTC`;
}

/** Format ISO date in user's local time for display, e.g. "Tue 5:00 PM" */
function formatSessionTimeLocal(isoUtc: string): string {
  const d = new Date(isoUtc);
  const weekday = d.toLocaleDateString(undefined, { weekday: "short" });
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", hour12: true });
  return `${weekday} ${time}`;
}

export interface CurrentSessionProps {
  /** Instrument symbol, e.g. "XAU/USD" (slash is normalized for API) */
  instrument: string;
  /** Optional label before session text */
  showLabel?: boolean;
}

/** Next session start from definitions (when market is open) */
interface NextSessionStart {
  name: string;
  startUtc: string;
}

export default function CurrentSession({ instrument, showLabel = true }: CurrentSessionProps) {
  const [state, setState] = useState<MarketSessionCurrentState | null>(null);
  const [nextSession, setNextSession] = useState<NextSessionStart | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const actionBarBg = useColorModeValue("white", "#151517");
  const actionBarBorderColor = useColorModeValue("gray.200", "gray.600");
  const secondaryTextColor = useColorModeValue("gray.400", "white");
  const openColor = "green.500";
  const closedColor = "orange.500";
  const warningColor = "orange.400";

  useEffect(() => {
    if (!instrument?.trim()) return;

    const fetchState = async () => {
      try {
        setError(null);
        const data = await getMarketSessionCurrentState(instrument);
        setState(data);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load session");
        setState(null);
      }
    };

    fetchState();
    const interval = setInterval(fetchState, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [instrument]);

  // When market is open, fetch definitions for today + tomorrow to get next session start
  useEffect(() => {
    if (!instrument?.trim() || !state?.is_market_open) {
      setNextSession(null);
      return;
    }
    const nowDate = new Date();
    const today = nowDate.toISOString().slice(0, 10);
    const tomorrow = new Date(nowDate.getTime() + 86400000).toISOString().slice(0, 10);
    let cancelled = false;
    (async () => {
      try {
        const [resToday, resTomorrow] = await Promise.all([
          getMarketSessionDefinitions(instrument, today),
          getMarketSessionDefinitions(instrument, tomorrow),
        ]);
        if (cancelled) return;
        const all: MarketSessionDefinition[] = [
          ...(resToday.sessions || []),
          ...(resTomorrow.sessions || []),
        ];
        const nowMs = Date.now();
        const next = all
          .filter((d) => new Date(d.start_utc).getTime() > nowMs)
          .sort((a, b) => new Date(a.start_utc).getTime() - new Date(b.start_utc).getTime())[0];
        if (next) {
          setNextSession({
            name: next.name,
            startUtc: next.start_utc,
          });
        } else {
          setNextSession(null);
        }
      } catch {
        if (!cancelled) setNextSession(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [instrument, state?.is_market_open, state?.timestamp_utc]);

  // Tick "now" so countdown updates (faster when under 1h left)
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), TICK_FAST_MS);
    return () => clearInterval(interval);
  }, []);

  if (error) {
    return (
      <Tooltip label={error} hasArrow placement="top">
        <Box
          px={3}
          py={1.5}
          h="32px"
          display="inline-flex"
          alignItems="center"
          borderRadius="md"
          border="1px solid"
          borderColor={actionBarBorderColor}
          bg={actionBarBg}
        >
          <Text fontSize="xs" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif">
            Session —
          </Text>
        </Box>
      </Tooltip>
    );
  }

  if (!state) {
    return (
      <Box
        px={3}
        py={1.5}
        h="32px"
        display="inline-flex"
        alignItems="center"
        borderRadius="md"
        border="1px solid"
        borderColor={actionBarBorderColor}
        bg={actionBarBg}
      >
        <Text fontSize="xs" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif">
          Session …
        </Text>
      </Box>
    );
  }

  const sessionLabel =
    state.current_sessions && state.current_sessions.length > 0
      ? state.current_sessions.map((s) => s.name).join(" · ")
      : "—";
  const openLabel = state.is_market_open ? "Open" : "Closed";
  const statusColor = state.is_market_open ? openColor : closedColor;

  // Always show "Next session: [Name] in X (time)" — next session start or Sydney when next is weekend open; time in local with UTC in tooltip
  const nowMs = now;
  let countdownLabel: string | null = null;
  let countdownTooltip: string | null = null;
  let countdownMs: number | null = null;

  if (state.is_market_open) {
    const closeUtc = state.next_week_close_utc ? new Date(state.next_week_close_utc).getTime() : null;
    const nextSessionMs = nextSession ? new Date(nextSession.startUtc).getTime() - nowMs : null;
    const timeUntilClose = closeUtc != null ? closeUtc - nowMs : null;
    // Next session starting before weekend close
    if (nextSession && nextSessionMs != null && nextSessionMs > 0 && (timeUntilClose == null || nextSessionMs < timeUntilClose)) {
      countdownMs = nextSessionMs;
      const atTimeLocal = formatSessionTimeLocal(nextSession.startUtc);
      const atTimeUtc = formatSessionTimeUtc(nextSession.startUtc);
      countdownLabel = `Next session: ${nextSession.name} in ${formatTimeLeft(countdownMs)} (${atTimeLocal})`;
      countdownTooltip = atTimeUtc;
    } else if (state.next_week_open_utc) {
      // No more sessions this week — next session is Sydney (market open Sunday)
      countdownMs = new Date(state.next_week_open_utc).getTime() - nowMs;
      if (countdownMs > 0) {
        const atTimeLocal = formatSessionTimeLocal(state.next_week_open_utc);
        const atTimeUtc = formatSessionTimeUtc(state.next_week_open_utc);
        countdownLabel = `Next session: Sydney in ${formatTimeLeft(countdownMs)} (${atTimeLocal})`;
        countdownTooltip = atTimeUtc;
      }
    }
  } else if (state.next_week_open_utc) {
    countdownMs = new Date(state.next_week_open_utc).getTime() - nowMs;
    if (countdownMs > 0) {
      const atTimeLocal = formatSessionTimeLocal(state.next_week_open_utc);
      const atTimeUtc = formatSessionTimeUtc(state.next_week_open_utc);
      countdownLabel = `Next session: Sydney in ${formatTimeLeft(countdownMs)} (${atTimeLocal})`;
      countdownTooltip = atTimeUtc;
    }
  }

  const isSoon = countdownMs != null && countdownMs > 0 && countdownMs < 3600000; // under 1h

  const tooltipBody =
    state.next_week_open_utc || state.next_week_close_utc
      ? `Next open: ${state.next_week_open_utc ? new Date(state.next_week_open_utc).toLocaleString() : "—"} | Next close: ${state.next_week_close_utc ? new Date(state.next_week_close_utc).toLocaleString() : "—"}`
      : undefined;

  return (
    <Tooltip label={tooltipBody} hasArrow placement="top">
      <VStack
        align="stretch"
        spacing={0}
        px={3}
        py={1.5}
        minH="32px"
        borderRadius="md"
        border="1px solid"
        borderColor={actionBarBorderColor}
        bg={actionBarBg}
        flexShrink={0}
      >
        <HStack spacing={2} flexWrap="nowrap">
          {showLabel && (
            <Text
              fontSize="xs"
              color={secondaryTextColor}
              fontFamily="'IBM Plex Sans', sans-serif"
              fontWeight="medium"
              textTransform="uppercase"
              whiteSpace="nowrap"
            >
              Session
            </Text>
          )}
          <Text
            fontSize="xs"
            color={secondaryTextColor}
            fontFamily="'IBM Plex Sans', sans-serif"
            fontWeight="medium"
            whiteSpace="nowrap"
            noOfLines={1}
          >
            {sessionLabel}
          </Text>
          <Box
            w="6px"
            h="6px"
            borderRadius="full"
            bg={statusColor}
            flexShrink={0}
            title={openLabel}
          />
          <Text
            fontSize="xs"
            fontFamily="'IBM Plex Sans', sans-serif"
            fontWeight="medium"
            color={statusColor}
            whiteSpace="nowrap"
          >
            {openLabel}
          </Text>
        </HStack>
        {countdownLabel && (
          countdownTooltip ? (
            <Tooltip label={`UTC: ${countdownTooltip}`} hasArrow placement="top" fontSize="xs">
              <Text
                fontSize="xs"
                fontFamily="'IBM Plex Sans', sans-serif"
                fontWeight="medium"
                color={isSoon ? warningColor : secondaryTextColor}
                whiteSpace="nowrap"
                mt={0.5}
                as="span"
                display="inline-block"
                borderBottom="1px dotted currentColor"
                cursor="help"
              >
                {countdownLabel}
              </Text>
            </Tooltip>
          ) : (
            <Text
              fontSize="xs"
              fontFamily="'IBM Plex Sans', sans-serif"
              fontWeight="medium"
              color={isSoon ? warningColor : secondaryTextColor}
              whiteSpace="nowrap"
              mt={0.5}
            >
              {countdownLabel}
            </Text>
          )
        )}
      </VStack>
    </Tooltip>
  );
}
